/**
 * One read per URL for idempotent entity GETs issued by many inputs at once.
 *
 * A form load marshals every input several times, and every marshal re-resolves the records its inputs
 * display, so the same `<Entity>s/FilteredTo/...` URL is requested by dozens of inputs within a few
 * milliseconds. Two layers keep that to one request:
 *
 *   - In flight: a caller asking for a URL that is already being fetched waits on that request.
 *     Always on - callers issuing the same GET at the same moment would receive the same rows anyway.
 *   - Result reuse (opt-in, `ReuseResult`): a non-empty response is kept for RESULT_TTL_MS and handed
 *     to later callers. Empty responses and errors are never kept, so a record created mid-session is
 *     found on the next read.
 *
 * Every caller receives its own copy of the rows: callers write fetched records straight into form data.
 * `invalidate` drops both layers (a request already in flight still answers its own waiters); the save
 * lifecycle calls it so nothing read before a save is served after it.
 *
 * State is per pict instance.
 *
 * @module _SharedEntityRead
 */

/** Matches pict's EntityProvider record cache (Pict-Meadow-EntityProvider.js initializeCache). */
const RESULT_TTL_MS = 30000;

/**
 * @typedef {Object} SharedReadEntry
 * @property {Array<(pError: any, pBody?: any) => void>} Waiters
 * @property {number} Generation
 */

/**
 * @typedef {Object} SharedReadState
 * @property {Map<string, SharedReadEntry>} InFlight
 * @property {Map<string, { Body: any, StoredAt: number }>} Results
 * @property {number} Generation
 */

/** @type {WeakMap<Object, SharedReadState>} */
const _States = new WeakMap();

/**
 * @param {Object} pPict
 * @return {SharedReadState}
 */
function getState(pPict)
{
	let tmpState = _States.get(pPict);
	if (!tmpState)
	{
		tmpState = { InFlight: new Map(), Results: new Map(), Generation: 0 };
		_States.set(pPict, tmpState);
	}
	return tmpState;
}

/**
 * @param {any} pBody
 * @return {any}
 */
function copyBody(pBody)
{
	return (pBody && typeof pBody === 'object') ? JSON.parse(JSON.stringify(pBody)) : pBody;
}

/**
 * GET a URL through `pict.HeadlightRestClient.getJSON`, sharing the request with identical concurrent callers.
 *
 * @param {Object} pPict - The pict instance (owns HeadlightRestClient).
 * @param {string} pURL - The request URL, exactly as it would be passed to getJSON.
 * @param {{ ReuseResult?: boolean }} pOptions - ReuseResult: accept a kept response younger than RESULT_TTL_MS.
 * @param {(pError: any, pBody?: any) => void} fCallback - getJSON-shaped callback; the body is the caller's own copy.
 */
function readJSON(pPict, pURL, pOptions, fCallback)
{
	const tmpState = getState(pPict);
	if (pOptions && pOptions.ReuseResult)
	{
		const tmpKept = tmpState.Results.get(pURL);
		if (tmpKept && (Date.now() - tmpKept.StoredAt) < RESULT_TTL_MS)
		{
			return fCallback(null, copyBody(tmpKept.Body));
		}
	}
	const tmpPending = tmpState.InFlight.get(pURL);
	if (tmpPending)
	{
		tmpPending.Waiters.push(fCallback);
		return;
	}
	/** @type {SharedReadEntry} */
	const tmpEntry = { Waiters: [ fCallback ], Generation: tmpState.Generation };
	tmpState.InFlight.set(pURL, tmpEntry);
	pPict.HeadlightRestClient.getJSON(pURL, (pError, pBody) =>
	{
		if (tmpState.InFlight.get(pURL) === tmpEntry)
		{
			tmpState.InFlight.delete(pURL);
		}
		const tmpHasRows = Array.isArray(pBody) ? pBody.length > 0 : (pBody && typeof pBody === 'object');
		if (!pError && tmpHasRows && tmpEntry.Generation === tmpState.Generation)
		{
			tmpState.Results.set(pURL, { Body: copyBody(pBody), StoredAt: Date.now() });
		}
		for (const fWaiter of tmpEntry.Waiters)
		{
			fWaiter(pError, pError ? pBody : copyBody(pBody));
		}
	});
}

/**
 * Drop kept responses and detach in-flight requests, so every later read goes to the server.
 *
 * @param {Object} pPict - The pict instance.
 */
function invalidate(pPict)
{
	const tmpState = getState(pPict);
	tmpState.Results.clear();
	tmpState.InFlight.clear();
	tmpState.Generation++;
}

/** Meadow's schema default for an unset GUID column; no row is ever looked up by it. */
const PLACEHOLDER_GUID = '0x0000000000000000';

/**
 * Whether an identity value names no record: empty, a database id of 0 (ids start at 1), or the
 * placeholder GUID. A comma list is empty only when every member is.
 *
 * @param {string} pIdentityField - The column the value is matched on (e.g. IDUser, GUIDSample, Hash).
 * @param {any} pValue - The value, or a comma-separated list of values.
 * @return {boolean}
 */
function isEmptyIdentityValue(pIdentityField, pValue)
{
	if (pValue === null || pValue === undefined)
	{
		return true;
	}
	const tmpIsDatabaseID = /^ID[A-Z]/.test(String(pIdentityField || ''));
	const tmpIsGUID = /^GUID[A-Z]/.test(String(pIdentityField || ''));
	return String(pValue).split(',').every((pPart) =>
	{
		const tmpPart = pPart.trim();
		return tmpPart === '' || (tmpIsDatabaseID && Number(tmpPart) === 0) || (tmpIsGUID && tmpPart === PLACEHOLDER_GUID);
	});
}

/**
 * Whether a read by ID returned the record asked for. pict's EntityProvider.getEntity hands the server's error
 * reply (`{ Error, ErrorCode }`) back in the record's place, so a reply without its own `ID<Entity>` is a failed
 * read.
 *
 * @param {string} pEntity - The entity type that was read.
 * @param {any} pRecord - The reply.
 * @return {boolean}
 */
function isEntityRecord(pEntity, pRecord)
{
	if (!pRecord || typeof pRecord !== 'object' || Array.isArray(pRecord))
	{
		return false;
	}
	const tmpIdentityField = `ID${pEntity}`;
	return !isEmptyIdentityValue(tmpIdentityField, pRecord[tmpIdentityField]);
}

module.exports = { readJSON, invalidate, isEmptyIdentityValue, isEntityRecord, RESULT_TTL_MS };
